import ContentHandler from 'owa-controls-content-handler/lib/schema/ContentHandler';
import { format as formatUrl, parse as parseUrl } from 'url';
import {
    getCardTypeFromCardDetails,
    getCardOamAppProvider,
    CardDetails,
} from 'owa-actionable-message';

//TODO: Formalize this framework
export type Experiments = { name: 'rp-acDisableTransactionCard'; flight: boolean };

const CLICK_EVENT_NAME = 'click';
const ADAPTIVE_CARD_LINK_HANDLER_SELECTOR = 'a';
const UTM_MEDIUM_TAG = 'utm_medium';
const UTM_BASE = 'outlookreact';

// using a feature map - mapping the experiments name to the exact medium extension
const UTMFeatureMap = {
    'rp-acDisableTransactionCard': 'nocard',
};

export const ADAPTIVE_CARD_LINK_HANDLER_NAME = 'adaptiveCardLinkHandler';

//TODO: make this handler compliant with multiple cards
export class AdaptiveCardLinkHandler implements ContentHandler {
    public readonly cssSelector = ADAPTIVE_CARD_LINK_HANDLER_SELECTOR;
    public readonly keywords = null;
    private experiments: Experiments;

    private boundElements: {
        element: HTMLElement;
        handler: (clickEvent: MouseEvent) => void;
    }[];

    /**
     * Resolves the tags denoted for a specific card type
     *
     * @param {string} cardType the card type
     * @returns {{}} {} iff cardType is handled, otherwise {}
     */
    private resolveCardTypeTags(cardType: string): {} {
        let tags = {};
        switch (cardType) {
            case 'Transaction':
                //NOTE: if you are adding additional tags, dont forget merge the previously
                // created tags - and change the function return signature
                tags[UTM_MEDIUM_TAG] =
                    UTM_BASE + (this.experiments.flight && UTMFeatureMap[this.experiments.name]);
                break;
            default:
                tags = {};
                break;
        }
        return tags;
    }

    constructor(
        private logLinkInteractionCallback: (evt: MouseEvent, cardDetails: CardDetails) => void,
        private cardDetails: CardDetails[],
        experiments: Experiments
    ) {
        this.boundElements = [];
        this.experiments = experiments;
    }

    public readonly handler = (element: HTMLElement, keyword?: string) => {
        if (!this.cardDetails || this.cardDetails.length < 1) {
            return;
        }

        // fetch the card type from the card details
        const oamAppProvider = getCardOamAppProvider(this.cardDetails[0]);
        const cardType = getCardTypeFromCardDetails(this.cardDetails[0]);

        const clickHandler = (evt: MouseEvent) => {
            // Default logging to the first card in the cardDetails array
            if (this.logLinkInteractionCallback) {
                this.logLinkInteractionCallback(evt, this.cardDetails[0]);
            }

            const { href } = element as HTMLAnchorElement;

            if (href && cardType && cardType != 'Unknown') {
                // The use of a switch statement over a if-else block allows additional AdaptiveCards to
                // register under getCardType, then utilize this link handler to append additional utm tags
                let tags: any;
                switch (oamAppProvider) {
                    case 'XeroOpay':
                        tags = this.resolveCardTypeTags(cardType);
                        break;
                    default:
                        break;
                }

                // If we have custom tags, then override the default behaviour for opening the link
                if (tags) {
                    evt.preventDefault();
                    // By using the url packages parse function, we can leave null checking to the .search api.
                    // this was used rather than using a manual falsey check on the queryBuilder object
                    const linkUrl = parseUrl(href, true);

                    // Test the link for existing tags, modify the link, and execute the default action
                    // https://nodejs.org/api/url.html#url_url_parse_urlstring_parsequerystring_slashesdenotehost
                    //FIXME: handle object collisions
                    // Current behaviour: right-most will win out - i.e. whatever value in the tag object
                    linkUrl.query = {
                        ...linkUrl.query,
                        ...tags,
                    };

                    linkUrl.search = undefined; //See: https://nodejs.org/api/url.html#url_url_format_urlobject

                    window.open(formatUrl(linkUrl), '_blank');
                }
            }
        };

        element.addEventListener(CLICK_EVENT_NAME, clickHandler);
        this.boundElements.push({ element: element, handler: clickHandler });
    };

    public readonly undoHandler = (elements: HTMLElement[]) => {
        this.boundElements.forEach(boundElement =>
            boundElement.element.removeEventListener(CLICK_EVENT_NAME, boundElement.handler)
        );
        this.boundElements = [];
    };
}
